import React, { useEffect, useState } from "react"
import Dialog from "@material-ui/core/Dialog"
import Button from "@material-ui/core/Button"
import IconButton from "@material-ui/core/IconButton"
import CloseIcon from "@material-ui/icons/Close"
import CircularProgress from "@material-ui/core/CircularProgress"
import { loadStripe } from "@stripe/stripe-js"
import history from "../../../../tools/history"
import { subscriptionDetails, buyCredits, addCredits } from "../../../../actions/client"
import { centsToUSD } from "../../../../tools/formatters"
import { Toast } from "components/shared/Toaster"
import OnboardingStepper from "components/FreeTrialWelcome/OnboardingStepper"
import "./planConfirmationModal.scss"
import { getIncludedCredits } from "constants/plans"

// Payment step is the current one here (vs. "skipped" on the free path).
const STEPS = [
  { label: "Select plan", state: "done" },
  { label: "Set up account", state: "done" },
  { label: "Confirm email", state: "done" },
  { label: "Payment & billing", state: "active" },
  { label: "Into the platform", state: "upcoming" },
]

const CREDIT_PACKS = [100, 250, 500, 1000]

// Plan activation is async (Stripe customer.subscription.created webhook → confirmSub).
// Poll subscription details until the paid subscription is active so the plan name,
// included credits, and per-credit price shown/charged reflect the NEW plan, not Trial.
const POLL_ATTEMPTS = 8
const POLL_DELAY_MS = 1500

/**
 * Post-payment confirmation shown when a paid subscription becomes active
 * (Stripe returnUrl → /settings/showConfirmation/true). Two states:
 *  A) welcome to the plan, with the option to add credits now;
 *  B) a one-time credit-pack picker that charges via the existing buy-credits flow.
 */
const StripeConfirmationModal = ({ open, onClose, onBillingChanged = () => {} }) => {
  const [view, setView] = useState("welcome") // "welcome" | "credits" | "platform"
  const [sub, setSub] = useState(null)
  const [selectedPack, setSelectedPack] = useState(500)
  const [custom, setCustom] = useState(false)
  const [customAmount, setCustomAmount] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [loadingPlan, setLoadingPlan] = useState(false)

  useEffect(() => {
    if (!open) return undefined
    setView("welcome")
    setError("")
    setSub(null)
    setLoadingPlan(true)

    let cancelled = false
    let attempts = 0
    let timerId = null
    const poll = () => {
      subscriptionDetails()
        .then((res) => {
          if (cancelled) return
          setSub(res)
          attempts += 1
          // Stop once the paid subscription is active, or after we run out of attempts.
          if (res?.hasSubscriptionId || attempts >= POLL_ATTEMPTS) {
            setLoadingPlan(false)
            // Subscription is now active → refresh the billing tab behind the modal.
            if (res?.hasSubscriptionId) onBillingChanged()
          } else {
            timerId = setTimeout(poll, POLL_DELAY_MS)
          }
        })
        .catch(() => {
          if (cancelled) return
          attempts += 1
          if (attempts >= POLL_ATTEMPTS) setLoadingPlan(false)
          else timerId = setTimeout(poll, POLL_DELAY_MS)
        })
    }
    poll()

    return () => {
      cancelled = true
      if (timerId) clearTimeout(timerId)
    }
  }, [open]) // eslint-disable-line react-hooks/exhaustive-deps

  const planName = sub?.currentPlan
  const creditCost = sub?.creditCostInCents
  const included = getIncludedCredits(planName)

  const amount = custom ? Number(customAmount) : selectedPack
  const validAmount = Number.isInteger(amount) && amount > 0
  const priceLabel =
    validAmount && creditCost ? `${amount} credits, ${centsToUSD(amount * creditCost)} one-time` : ""

  // Buying or skipping doesn't leave immediately anymore: it advances to the
  // final "Into the platform" step (view B/C), where the user picks where to land.
  const goToStepFive = () => {
    setError("")
    setView("platform")
  }

  const goToDashboard = () => {
    onClose()
    history.push("/dashboard")
  }

  const runFirstApplication = () => {
    onClose()
    history.push("/create-applicant")
  }

  const addCreditsNow = async () => {
    if (!validAmount || !creditCost) return
    const amountInCents = amount * creditCost
    setLoading(true)
    setError("")
    try {
      const res = await buyCredits({ amountInCents: Number(amountInCents), amountOfCredits: Number(amount) })

      // Card requires extra confirmation: only proceed to success once the intent
      // actually succeeds AND the credits are added (mirrors BuyCreditsDialog).
      if (res.status === "REQUIRES_ACTION") {
        const stripe = await loadStripe(process.env.REACT_APP_STRIPE_PK)
        const result = await stripe.confirmCardPayment(res.sessionCode)
        if (result.error) {
          setError(result.error.message)
          setLoading(false)
          return
        }
        if (result.paymentIntent?.status !== "succeeded") {
          // e.g. "processing" / "requires_payment_method": payment did not complete.
          setError("Your payment could not be completed. Please try again.")
          setLoading(false)
          return
        }
        try {
          await addCredits({
            amountInCents: Number(amountInCents),
            amountOfCredits: Number(amount),
            paymentIntentId: result.paymentIntent.id,
          })
        } catch (e) {
          setError("Payment succeeded but credits could not be added. Please contact support.")
          setLoading(false)
          return
        }
      }

      // Success: either no extra action was needed, or the card was confirmed and credits added.
      setLoading(false)
      onBillingChanged()
      Toast({ type: "success", message: `${amount} credits purchased and loaded successfully.` })
      goToStepFive()
    } catch (e) {
      setError("An error occurred while processing your payment. Please try again.")
      setLoading(false)
    }
  }

  // On the final step, mark payment as done and light up "Into the platform".
  const stepperSteps =
    view === "platform"
      ? STEPS.map((s, i) => (i === 3 ? { ...s, state: "done" } : i === 4 ? { ...s, state: "active" } : s))
      : STEPS

  // Only the X button (or an explicit action) closes this modal — clicking the
  // backdrop is ignored so the onboarding step isn't dismissed by accident.
  const handleDialogClose = (e, reason) => {
    if (reason === "backdropClick") return
    onClose()
  }

  return (
    <Dialog open={open} onClose={handleDialogClose} maxWidth="sm" fullWidth PaperProps={{ className: "pc-dialog-paper" }}>
      <div className="pc-content">
        <IconButton className="pc-close" aria-label="Close" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <OnboardingStepper steps={stepperSteps} />

        {loadingPlan ? (
          <div className="pc-loading">
            <CircularProgress size={28} />
            <p className="pc-subtitle" style={{ marginTop: 16, marginBottom: 0 }}>
              Finalizing your subscription…
            </p>
          </div>
        ) : (
        <>
        <h2 className="pc-title">
          {view === "platform"
            ? "You're all set"
            : planName ? `You're all set on ${planName}` : "You're all set"}
        </h2>
        <p className="pc-subtitle">
          {view === "platform"
            ? "You're ready to go — run your first application or explore the platform."
            : included ? `Your plan includes ${included} credits` : "Your plan is ready to go."}
        </p>

        {view === "platform" && (
          <>
            <Button fullWidth className="pc-cta" onClick={runFirstApplication}>
              Run your first application
            </Button>
            <Button fullWidth className="pc-secondary-btn" onClick={goToDashboard}>
              Explore the platform
            </Button>
          </>
        )}

        {view === "welcome" && (
          <>
            <Button fullWidth className="pc-cta" onClick={() => setView("credits")}>
              Add more credits
            </Button>
            <Button fullWidth className="pc-secondary-btn" onClick={goToStepFive}>
              Skip, take me to the platform
            </Button>
          </>
        )}

        {view === "credits" && (
          <>
            <div className="pc-pack-label">Choose a credit pack</div>
            <div className="pc-packs">
              {CREDIT_PACKS.map((p) => (
                <button
                  type="button"
                  key={p}
                  className={`pc-pack ${!custom && selectedPack === p ? "pc-pack--selected" : ""}`}
                  onClick={() => {
                    setCustom(false)
                    setSelectedPack(p)
                  }}
                >
                  +{p}
                </button>
              ))}
              <button
                type="button"
                className={`pc-pack ${custom ? "pc-pack--selected" : ""}`}
                onClick={() => setCustom(true)}
              >
                Custom
              </button>
            </div>

            {custom && (
              <input
                type="number"
                min={1}
                step={1}
                className="pc-custom-input"
                placeholder="Enter amount"
                value={customAmount}
                onChange={(e) => setCustomAmount(e.target.value === "" ? "" : Number(e.target.value))}
              />
            )}

            <div className="pc-summary">{priceLabel || "Select an amount"}</div>

            {error && <div className="pc-error">{error}</div>}

            <Button
              fullWidth
              className="pc-cta"
              onClick={addCreditsNow}
              disabled={loading || !validAmount || !creditCost}
            >
              {loading ? <CircularProgress size={22} color="inherit" /> : "Add credits"}
            </Button>

            <div className="pc-skip-link" role="button" tabIndex={0} onClick={goToStepFive}>
              Skip for now
            </div>

            <div className="pc-footnote">This is a one-time charge. Credits never expire.</div>
          </>
        )}
        </>
        )}
      </div>
    </Dialog>
  )
}

export default StripeConfirmationModal
